import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, Check, Bell, ExternalLink, Zap,
  Mic, BarChart3, Palette, Settings, Code, Search as SearchIcon,
  Film, Home, User, Plus, ShoppingBag, X
} from "lucide-react";
import api from "../lib/api";

const CATEGORY_ICONS = {
  voice: Mic, productivity: BarChart3, design: Palette,
  automation: Settings, development: Code, search: SearchIcon, media: Film,
};

const CATEGORY_COLORS = {
  voice: "text-pink-400 bg-pink-500/10 border-pink-500/30",
  productivity: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30",
  design: "text-violet-400 bg-violet-500/10 border-violet-500/30",
  automation: "text-amber-400 bg-amber-500/10 border-amber-500/30",
  development: "text-blue-400 bg-blue-500/10 border-blue-500/30",
  search: "text-teal-400 bg-teal-500/10 border-teal-500/30",
  media: "text-cyan-400 bg-cyan-500/10 border-cyan-500/30",
};

const CATEGORIES = [
  { id: "", label: "All" },
  { id: "voice", label: "Voice" },
  { id: "productivity", label: "Productivity" },
  { id: "design", label: "Design" },
  { id: "automation", label: "Automation" },
  { id: "development", label: "Development" },
  { id: "search", label: "Search" },
  { id: "media", label: "Media" },
];

export default function IntegrationsPage() {
  const navigate = useNavigate();
  const [integrations, setIntegrations] = useState([]);
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("");
  const [selected, setSelected] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/integrations").then((r) => setIntegrations(r.data)),
      api.get("/integrations/my-connections").then((r) => setConnections(r.data)).catch(() => {}),
      api.get("/profile/me").then((r) => setProfile(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  const isConnected = (slug) => connections.some((c) => c.slug === slug);
  const filtered = category ? integrations.filter((i) => i.category === category) : integrations;

  const handleConnect = async (slug) => {
    setConnecting(true);
    try {
      await api.post(`/integrations/${slug}/connect`);
      setConnections((prev) => [...prev, { slug, connected_at: new Date().toISOString() }]);
      toast.success("Connected! This integration is now active.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to connect");
    } finally {
      setConnecting(false);
    }
  };

  const handleNotify = (name) => {
    toast.success(`We'll notify you when ${name} integration is available!`);
  };

  return (
    <div className="min-h-screen pb-20 lg:pb-0" data-testid="integrations-page">
      {/* Top Bar */}
      <div className="sticky top-0 z-30 nav-glass px-4">
        <div className="max-w-5xl mx-auto flex items-center gap-3 h-12">
          <button onClick={() => navigate("/feed")} className="text-muted-foreground hover:text-foreground/80 bg-transparent border-0 cursor-pointer"><ArrowLeft className="w-5 h-5" /></button>
          <Zap className="w-4 h-4 text-cyan-400" />
          <h1 className="font-outfit text-sm font-semibold text-foreground">Integrations</h1>
        </div>
      </div>

      {/* Hero */}
      <div className="relative py-12 px-4 overflow-hidden" data-testid="integrations-hero">
        <div className="absolute inset-0">
          <img src="https://static.prod-images.emergentagent.com/jobs/69522626-12bf-4c16-9d63-ce7a21ed458c/images/b3dde32a10483e876da092e1df74e1ad628785894ad8a17f26ab45534416a97b.jpeg" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-indigo-900/80 via-purple-900/70 to-pink-900/60" />
        </div>
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <h2 className="font-outfit text-2xl sm:text-3xl font-semibold text-white mb-2">Supercharge Your Experience</h2>
          <p className="text-sm text-white/80 max-w-md mx-auto">Connect your favorite tools to App-Mi.ai and unlock powerful AI-driven workflows.</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Category pills */}
        <div className="flex gap-1.5 mb-6 overflow-x-auto pb-1">
          {CATEGORIES.map((c) => (
            <button key={c.id} onClick={() => setCategory(c.id)} className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap border-0 cursor-pointer transition-all ${category === c.id ? "bg-indigo-500 text-white" : "bg-card text-muted-foreground hover:bg-white/5 border border-border"}`} data-testid={`cat-${c.id || "all"}`}>
              {c.label}
            </button>
          ))}
        </div>

        {/* Grid */}
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-cyan-400 animate-spin" /></div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="integrations-grid">
            {filtered.map((item) => {
              const CatIcon = CATEGORY_ICONS[item.category] || Zap;
              const colors = CATEGORY_COLORS[item.category] || "text-muted-foreground bg-muted/40 border-border";
              const connected = isConnected(item.slug);
              return (
                <div
                  key={item.slug}
                  className="bg-card border border-border/60 rounded-xl p-5 card-hover cursor-pointer"
                  onClick={() => setSelected(item)}
                  data-testid={`integration-${item.slug}`}
                >
                  <div className="flex items-start gap-3 mb-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center border ${colors}`}>
                      <CatIcon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-semibold text-foreground">{item.name}</h3>
                      <span className={`inline-block text-[10px] px-2 py-0.5 rounded-full capitalize mt-0.5 ${colors}`}>{item.category}</span>
                    </div>
                    {item.is_available ? (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 font-medium whitespace-nowrap" data-testid={`status-${item.slug}`}>Available</span>
                    ) : (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-muted/70 text-muted-foreground font-medium whitespace-nowrap" data-testid={`status-${item.slug}`}>Coming Soon</span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground line-clamp-2 mb-3">{item.description}</p>
                  {item.is_available ? (
                    connected ? (
                      <div className="flex items-center gap-1 text-xs text-emerald-400 font-medium" data-testid={`connected-${item.slug}`}><Check className="w-3.5 h-3.5" /> Connected</div>
                    ) : (
                      <Button onClick={(e) => { e.stopPropagation(); handleConnect(item.slug); }} disabled={connecting} className="bg-gradient-to-r from-cyan-400 to-violet-500 text-white text-xs h-8 px-4 rounded-lg w-full press-down" data-testid={`connect-${item.slug}`}>
                        Connect
                      </Button>
                    )
                  ) : (
                    <Button onClick={(e) => { e.stopPropagation(); handleNotify(item.name); }} variant="outline" className="border-border text-muted-foreground text-xs h-8 px-4 rounded-lg w-full bg-card" data-testid={`notify-${item.slug}`}>
                      <Bell className="w-3 h-3 mr-1" /> Notify Me
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selected && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setSelected(null)} data-testid="integration-detail-modal">
          <div className="bg-card rounded-2xl shadow-xl max-w-lg w-full max-h-[85vh] overflow-y-auto animate-fade-in-up" onClick={(e) => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  {(() => {
                    const CatIcon = CATEGORY_ICONS[selected.category] || Zap;
                    const colors = CATEGORY_COLORS[selected.category] || "text-muted-foreground bg-muted/40";
                    return (
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${colors}`}>
                        <CatIcon className="w-6 h-6" />
                      </div>
                    );
                  })()}
                  <div>
                    <h2 className="font-outfit text-lg font-semibold text-foreground">{selected.name}</h2>
                    <span className="text-xs text-muted-foreground capitalize">{selected.category}</span>
                  </div>
                </div>
                <button onClick={() => setSelected(null)} className="text-muted-foreground/70 hover:text-muted-foreground bg-transparent border-0 cursor-pointer"><X className="w-5 h-5" /></button>
              </div>

              <p className="text-sm text-muted-foreground mb-4 leading-relaxed">{selected.description}</p>

              <h3 className="text-xs font-semibold text-foreground uppercase tracking-wider mb-2">Features</h3>
              <ul className="space-y-1.5 mb-5">
                {(selected.features || []).map((f, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Check className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" /> {f}
                  </li>
                ))}
              </ul>

              <div className="bg-cyan-500/10 rounded-xl p-4 mb-5">
                <h3 className="text-xs font-semibold text-cyan-200 mb-1">How it works with App-Mi.ai</h3>
                <p className="text-xs text-cyan-300 leading-relaxed">
                  {selected.is_available
                    ? `Connect ${selected.name} to enhance your AI companion experience. This integration works seamlessly with your existing companions and content.`
                    : `When available, ${selected.name} will integrate directly into your App-Mi.ai workflow, enhancing your companion's capabilities.`}
                </p>
              </div>

              <div className="flex items-center gap-3">
                {selected.is_available ? (
                  isConnected(selected.slug) ? (
                    <div className="flex items-center gap-2 text-sm text-emerald-400 font-medium"><Check className="w-4 h-4" /> Connected</div>
                  ) : (
                    <Button onClick={() => handleConnect(selected.slug)} disabled={connecting} className="bg-gradient-to-r from-cyan-400 to-violet-500 text-white text-sm h-10 px-6 rounded-lg flex-1 press-down" data-testid="modal-connect-btn">
                      {connecting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Connect Integration"}
                    </Button>
                  )
                ) : (
                  <Button onClick={() => { handleNotify(selected.name); setSelected(null); }} className="bg-muted/70 text-foreground/80 text-sm h-10 px-6 rounded-lg flex-1" data-testid="modal-notify-btn">
                    <Bell className="w-4 h-4 mr-2" /> Notify Me When Available
                  </Button>
                )}
                {selected.website_url && (
                  <a href={selected.website_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-cyan-400 no-underline hover:text-cyan-300" data-testid="integration-website-link">
                    <ExternalLink className="w-3.5 h-3.5" /> Website
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Bottom Tabs */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-card border-t border-border/60 h-14 flex items-center justify-around px-2">
        <Link to="/feed" className="flex flex-col items-center gap-0.5 no-underline text-muted-foreground/70"><Home className="w-5 h-5" /><span className="text-[10px]">Home</span></Link>
        <Link to="/marketplace" className="flex flex-col items-center gap-0.5 no-underline text-muted-foreground/70"><ShoppingBag className="w-5 h-5" /><span className="text-[10px]">Market</span></Link>
        <Link to="/feed" className="flex flex-col items-center gap-0.5 no-underline text-muted-foreground/70"><Plus className="w-5 h-5" /></Link>
        <Link to="/integrations" className="flex flex-col items-center gap-0.5 no-underline text-cyan-400"><Zap className="w-5 h-5" /><span className="text-[10px]">Integrations</span></Link>
        <Link to={profile ? `/profile/${profile.username}` : "/feed"} className="flex flex-col items-center gap-0.5 no-underline text-muted-foreground/70"><User className="w-5 h-5" /><span className="text-[10px]">Profile</span></Link>
      </div>
    </div>
  );
}
